import { Component, DestroyRef, OnDestroy, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { finalize, Subject } from 'rxjs';
import { DynamicFilterConfig, createDynamicFilterState } from '@card-software/library';
import { TranslateService } from '@ngx-translate/core';
import { MenuItem, SortEvent } from 'primeng/api';
import { PaginatorState } from 'primeng/paginator';
import {
  ApDocument,
  ApDocumentSearchCommand,
  ApDocumentStatus,
  ApDocumentSummary,
  ApDocumentType,
} from '../../../models/ap-document/ap-document';
import { ApDocumentProxyService } from '../../../proxies/ap-document.proxy.service';
import { EnumService } from '../../../services/enum.service';
import { ProviderFilterService } from '../../../services/provider-filter.service';

@Component({
  selector: 'app-ap-documents',
  templateUrl: './ap-documents.component.html',
  styleUrl: './ap-documents.component.scss',
})
export class ApDocumentsComponent implements OnDestroy {
  private readonly router = inject(Router);
  private readonly service = inject(ApDocumentProxyService);
  private readonly enumService = inject(EnumService);
  private readonly providerFilter = inject(ProviderFilterService);
  private readonly translateService = inject(TranslateService);

  private readonly destroy$ = new Subject<void>();
  private lastSearchCriteria: ApDocumentSearchCommand = {};

  private documentTypeLabels: Record<number, string> = {};
  /** Type-filter options excluding Retention (#4454) — retentions live on their own screen, so they are
   * never shown in the AP documents list or offered as a type filter here. */
  private documentTypeFilterLabels: Record<number, string> = {};
  private statusLabels: Record<number, string> = {};
  /** #6324 — a note's Motivo, for the Type column and the filter. */
  private noteReasonLabels: Record<number, string> = {};
  private currencyLabels: Record<number, string> = {};

  isLoading = signal(0);
  enumsReady = signal(false);
  filterConfig = signal<DynamicFilterConfig | null>(null);
  showFilters = signal(true);
  readonly filterState = createDynamicFilterState({ storageKey: 'ap-documents-filters', config: this.filterConfig });
  documents = signal<ApDocument[]>([]);
  summary = signal<ApDocumentSummary | null>(null);

  // Pagination
  currentPage = signal(1);
  pageSize = signal(25);
  /** Server-side sort (#5230). The table holds one page, so ordering has to happen in the query — a
   *  client-side sort would reorder 25 rows and present it as the order of all of them.
   *  Defaults match the search's own default: newest document date first. */
  readonly sortField = signal<string>('documentDate');
  readonly sortOrder = signal<number>(-1);
  totalRecords = signal(0);

  /** This screen's own type-ahead state — see ProviderFilterService.createConfig. */
  private readonly providerAutocompleteConfig = this.providerFilter.createConfig(inject(DestroyRef));

  // Speed-dial keeps the creation sub-type choice up-front: a plain Invoice, a Pre-Invoice registered
  // from an existing Order Invoice (#4306), or a Receipt (#4457/#4462 — vendor-issued recibo,
  // free-description amount-only lines).
  speedDialItems: MenuItem[] = [
    {
      label: this.translate('ApDocument_New_Invoice', 'Invoice'),
      icon: 'pi pi-file',
      command: () => this.redirectToDetails('new', 'INVOICE'),
    },
    {
      label: this.translate('ApDocument_New_PreInvoice', 'Pre Invoice'),
      icon: 'pi pi-file-import',
      command: () => this.redirectToDetails('new', 'INVOICE', 'preinvoice'),
    },
    {
      label: this.translate('ApDocument_New_Receipt', 'Receipt'),
      icon: 'pi pi-receipt',
      command: () => this.redirectToDetails('new', 'RECEIPT'),
    },
    {
      label: this.translate('ApDocument_New_PurchaseReceipt', 'Purchase Receipt'),
      icon: 'pi pi-ticket',
      command: () => this.redirectToDetails('new', 'PURCHASERECEIPT'),
    },
    {
      label: this.translate('ApDocument_New_Policy', 'Policy'),
      icon: 'pi pi-shield',
      command: () => this.redirectToDetails('new', 'POLICY'),
    },
    {
      label: this.translate('ApDocument_New_DebitNote', 'Debit Note'),
      icon: 'pi pi-plus-circle',
      command: () => this.redirectToDetails('new', 'DEBITNOTE'),
    },
    {
      label: this.translate('ApDocument_New_CreditNote', 'Credit Note'),
      icon: 'pi pi-minus-circle',
      command: () => this.redirectToDetails('new', 'CREDITNOTE'),
    },
  ];

  constructor() {
    this.filterState.applied$.pipe(takeUntilDestroyed()).subscribe((filters) => this.searchWithFilters(filters as ApDocumentSearchCommand));
    this.loadSummary();

    effect(
      () => {
        if (!this.enumService.enumsReady()) return;
        this.documentTypeLabels = this.enumService.getEnum('ApDocumentType').values as Record<number, string>;
        // Retention has its own screen (#4454) — drop it from the type filter options (labels stay for display).
        this.documentTypeFilterLabels = Object.fromEntries(
          Object.entries(this.documentTypeLabels).filter(([key]) => Number(key) !== ApDocumentType.Retention),
        ) as Record<number, string>;
        this.statusLabels = this.enumService.getEnum('ApDocumentStatus').values as Record<number, string>;
        this.currencyLabels = this.enumService.getEnum('Currency').values as Record<number, string>;
        this.noteReasonLabels = this.enumService.getEnum('ApNoteReason').values as Record<number, string>;
        this.enumsReady.set(true);
      },
      { allowSignalWrites: true },
    );

    effect(
      () => {
        if (this.translateService.currentLang && this.enumsReady()) {
          this.tryInitializeFilterConfig();
        }
      },
      { allowSignalWrites: true },
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private startLoading(): void {
    this.isLoading.update((v) => v + 1);
  }

  private stopLoading(): void {
    this.isLoading.update((v) => v - 1);
  }

  private translate(key: string, fallback: string): string {
    const translation = this.translateService.instant(key);
    return translation === key ? fallback : translation;
  }

  private tryInitializeFilterConfig(): void {
    this.filterConfig.set(this.buildFilterConfig());
  }

  private buildFilterConfig(): DynamicFilterConfig {
    return {
      persistInUrl: true,
      persistVisibility: true,
      filters: [
        {
          type: 'text',
          formControlName: 'number',
          label: this.translate('ApDocument_Number', 'Document #'),
          placeholder: this.translate('ApDocument_Number_Placeholder', 'Enter document number'),
          defaultVisible: true,
        },
        {
          type: 'text',
          formControlName: 'fiscalNumber',
          label: this.translate('ApDocument_FiscalInvoiceNumber', 'Fiscal invoice number'),
          placeholder: this.translate('ApDocument_FiscalInvoiceNumber_Placeholder', 'Enter fiscal invoice number'),
          defaultVisible: true,
        },
        {
          type: 'autocomplete',
          formControlName: 'providerReference',
          label: this.translate('ApPayment_Provider', 'Provider'),
          placeholder: this.translate('Provider_Search', 'Search provider'),
          autocompleteConfig: this.providerAutocompleteConfig,
          showClear: true,
          defaultVisible: true,
        },
        {
          type: 'select',
          formControlName: 'status',
          label: this.translate('Status', 'Status'),
          placeholder: this.translate('Select_status', 'Select status'),
          options: this.statusLabels,
          showClear: true,
          defaultVisible: true,
        },
        {
          type: 'select',
          formControlName: 'documentType',
          label: this.translate('Type', 'Type'),
          placeholder: this.translate('Select_type', 'Select type'),
          options: this.documentTypeFilterLabels,
          showClear: true,
          defaultVisible: false,
        },
        {
          type: 'select',
          formControlName: 'noteReason',
          label: this.translate('ApDocument_Note_Reason', 'Reason'),
          placeholder: this.translate('ApDocument_Note_Reason_Filter', 'Credit / debit note reason'),
          options: this.noteReasonLabels,
          showClear: true,
          defaultVisible: false,
        },
        {
          type: 'select',
          formControlName: 'currency',
          label: this.translate('Currency', 'Currency'),
          placeholder: this.translate('Select_currency', 'Select currency'),
          options: this.currencyLabels,
          showClear: true,
          defaultVisible: false,
        },
        {
          type: 'date',
          formControlName: 'documentDateFrom',
          label: this.translate('ApDocument_DocumentDateFrom', 'Doc date from'),
          defaultVisible: false,
        },
        {
          type: 'date',
          formControlName: 'documentDateTo',
          label: this.translate('ApDocument_DocumentDateTo', 'Doc date to'),
          defaultVisible: false,
        },
        {
          type: 'checkbox',
          formControlName: 'overdueOnly',
          label: this.translate('ApDocument_OverdueOnly', 'Overdue only'),
          defaultVisible: false,
        },
      ],
    };
  }

  private loadSummary(): void {
    // Company scope is resolved server-side from the current site.
    this.startLoading();
    this.service
      .getSummary()
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (response) => this.summary.set(response?.data ?? null),
        error: () => this.summary.set(null),
      });
  }

  private search(criteria: ApDocumentSearchCommand): void {
    this.lastSearchCriteria = criteria;
    this.startLoading();
    this.service
      .search(criteria, this.currentPage(), this.pageSize(), this.sortField(), this.sortOrder())
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (response) => {
          this.documents.set(response?.data?.items ?? []);
          this.totalRecords.set(response?.data?.totalCount ?? 0);
        },
        error: () => {
          this.documents.set([]);
          this.totalRecords.set(0);
        },
      });
  }

  private searchWithFilters(filters: ApDocumentSearchCommand): void {
    const criteria: ApDocumentSearchCommand = {
      ...filters,
      providerReference: this.providerFilter.toProviderId(filters.providerReference),
      documentType: filters.documentType != null ? Number(filters.documentType) : null,
      status: filters.status != null ? Number(filters.status) : null,
      currency: filters.currency != null ? Number(filters.currency) : null,
      noteReason: filters.noteReason != null ? Number(filters.noteReason) : null,
      documentDateFrom: this.toIso(filters.documentDateFrom),
      documentDateTo: this.toIso(filters.documentDateTo),
    };
    this.currentPage.set(1);
    this.search(criteria);
  }

  private toIso(value: unknown): string | null {
    if (!value) return null;
    return value instanceof Date ? value.toISOString() : String(value);
  }

  /**
   * Re-queries on a column sort (#5230). Back to page one, because a sorted page 3 is three pages into an
   * order that no longer exists. Guarded, so a sort that changed nothing does not re-fetch.
   */
  onSort(event: SortEvent): void {
    const field = event.field ?? 'documentDate';
    const order = event.order ?? -1;
    if (field === this.sortField() && order === this.sortOrder()) return;

    this.sortField.set(field);
    this.sortOrder.set(order);
    this.currentPage.set(1);
    this.search(this.lastSearchCriteria);
  }

  onPageChange(event: PaginatorState): void {
    this.currentPage.set((event.page ?? 0) + 1);
    this.pageSize.set(event.rows ?? this.pageSize());
    this.search(this.lastSearchCriteria);
  }

  redirectToDetails(id?: string, type?: string, source?: string): void {
    const queryParams: Record<string, string> = { id: id ?? 'new' };
    if (type) queryParams['type'] = type;
    if (source) queryParams['source'] = source;
    this.router.navigate(['/ap-document-details'], { queryParams });
  }

  /** #6324 — a note reads with its reason and the document it adjusts: "Nota de Crédito · Cantidad · AP-2026-…". */
  noteDetail(doc: ApDocument): string {
    const reason = doc.noteReason != null ? this.noteReasonLabels[doc.noteReason] : null;
    return [reason, doc.relatedDocumentReference?.number].filter((part): part is string => !!part).join(' · ');
  }

  /**
   * #6324 — a credit note is applied to its original when it posts, so "Paid" would say cash moved when none
   * did. Wholly applied reads "Applied"; one with credit left over reads "Open credit" — the provider's.
   */
  creditNoteState(doc: ApDocument): 'applied' | 'openCredit' | null {
    if (doc.documentType !== ApDocumentType.CreditNote) return null;
    if (doc.status === ApDocumentStatus.Draft || doc.status === ApDocumentStatus.Cancelled) return null;
    return (doc.balanceDue ?? 0) > 0 ? 'openCredit' : 'applied';
  }

  documentTypeLabel(type: number): string {
    return this.documentTypeLabels[type] ?? '';
  }

  statusLabel(status: number): string {
    return this.statusLabels[status] ?? '';
  }

  currencyLabel(currency: number | null | undefined): string {
    if (currency === null || currency === undefined) return '';
    return this.currencyLabels[currency] ?? '';
  }

  statusSeverity(status: number): 'success' | 'warn' | 'info' | 'danger' | 'secondary' {
    switch (status) {
      case ApDocumentStatus.Paid:
        return 'success';
      case ApDocumentStatus.Posted:
        return 'info';
      case ApDocumentStatus.PartiallyPaid:
        return 'warn';
      case ApDocumentStatus.Cancelled:
        return 'danger';
      default:
        return 'secondary';
    }
  }

  providerDisplay(document: ApDocument): string {
    return document.providerName || document.oneTimeVendorName || document.providerCode || '';
  }

  getRowData(rowData: unknown): ApDocument {
    return rowData as ApDocument;
  }
}
